import React, { useState, useEffect } from "react";
import { Keyboard, X, DeviceMobile } from "@phosphor-icons/react";
import { useShortcuts } from "@/context/ShortcutsContext";
import { displayCombo } from "@/lib/keyboardDisplay";

// v1.28.76 — Mobile helper. On phones we still show the cheat-sheet FAB
// (so anyone can see which combos are bound + confirm the feature exists)
// but explicitly note keyboard shortcuts only fire on desktop.

function detectTouch() {
    if (typeof window === "undefined") return false;
    try {
        if (window.matchMedia?.("(pointer: coarse)").matches) return true;
    } catch (_e) {}
    if (window.innerWidth < 768) return true;
    return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent || "");
}

/** Floating help FAB (bottom-left) that opens a card listing every
 *  active shortcut. Visible on every admin page. */
export default function ShortcutHelpFab() {
    const { shortcuts } = useShortcuts();
    const [open, setOpen] = useState(false);
    const [isTouch, setIsTouch] = useState(detectTouch);
    useEffect(() => {
        const onResize = () => setIsTouch(detectTouch());
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);
    const active = (shortcuts || []).filter((s) => s.combo);
    // Always render the FAB so users always know shortcuts exist, even
    // when none are bound (empty state gives the SU a nudge to configure).
    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="shortcut-help-fab"
                title="Keyboard shortcuts"
                className="fixed bottom-24 left-4 z-50 flex h-11 w-11 items-center justify-center rounded-full bg-slate-900 text-white shadow-lg ring-2 ring-white/70 transition hover:scale-105 hover:bg-slate-800 md:bottom-4"
            >
                <Keyboard size={20} weight="bold" />
            </button>
            {open && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
                     data-testid="shortcut-help-modal" role="dialog" aria-modal="true"
                     onClick={() => setOpen(false)}>
                    <div onClick={(e) => e.stopPropagation()}
                         className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
                        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
                            <div className="flex items-center gap-2">
                                <Keyboard size={16} weight="bold" className="text-slate-700" />
                                <div className="text-sm font-bold uppercase tracking-wider text-slate-700">Keyboard Shortcuts</div>
                            </div>
                            <button onClick={() => setOpen(false)} data-testid="shortcut-help-close" className="rounded p-1 text-slate-400 hover:bg-slate-100"><X size={14} /></button>
                        </div>
                        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
                            {isTouch && (
                                <div data-testid="shortcut-mobile-note" className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                    <DeviceMobile size={16} weight="fill" className="mt-0.5 shrink-0 text-amber-700" />
                                    <div>
                                        <b>Keyboard shortcuts are available on desktop only.</b>
                                        <div className="mt-0.5 opacity-80">Pair a Bluetooth keyboard with your device to use these combos, or open the app on a computer.</div>
                                    </div>
                                </div>
                            )}
                            {active.length === 0 ? (
                                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-xs text-slate-500">
                                    No shortcuts assigned yet.<br />
                                    A Super User can bind them under <b>Settings → Keyboard Shortcuts</b>.
                                </div>
                            ) : (
                                <ul className="space-y-2" data-testid="shortcut-help-list">
                                    {active.map((s) => (
                                        <li key={s.action_id} className="flex items-center justify-between text-sm">
                                            <span className="text-slate-800">{s.label}</span>
                                            <kbd className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[11px] font-bold text-slate-800 ring-1 ring-slate-200">
                                                {displayCombo(s.combo)}
                                            </kbd>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <div className="mt-4 text-[11px] text-slate-500">Configure these under <b>Settings → Keyboard Shortcuts</b> (Super User).</div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
